import { useCallback } from "react";
import { useAuthenticated, useSignOut } from "../auth";
import { useLicense, useSetLicense } from "../license";
import { sessionHeartbeatInterval } from "../config";
import { usePortalApi } from "../db/portal-api";
import { useNetworkStatus } from "../sync";
import { useInterval } from "../utils";

// Detects a session revoked server-side (e.g. another device logged in with the same,
// non-Mehrfachnutzung license — see /device/login) and signs this device out promptly
// instead of leaving it silently "logged in" with a dead portal key until some
// unrelated sync call happens to fail. Only checked while online, and network errors
// are ignored — offline devices must keep working undisturbed, and only an explicit
// 401 (session no longer exists) triggers sign-out.
export default function SessionHeartbeat() {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();
  const { effectiveType } = useNetworkStatus();
  const signOut = useSignOut();
  const license = useLicense();
  const setLicense = useSetLicense();

  const checkSession = useCallback(async () => {
    if (!useNewApi || !baseUrl || !portalKey) return;
    if (effectiveType === "none") return;

    try {
      const response = await fetch(`${baseUrl}/api/device/session`, {
        headers: { authorization: `Bearer ${portalKey}` },
      });
      if (response.status === 401) {
        await signOut(
          new Error(
            "Diese Lizenz wurde auf einem anderen Gerät angemeldet. Sie wurden daher hier abgemeldet.",
          ),
        );
      }

      // Settings PIN set/changed/removed in the portal — apply it without a new login.
      // Older portal versions don't send the field; then the lock stays as it is.
      if (response.ok && license) {
        const data = await response.json().catch(() => null);
        if (data && "settingsLock" in data) {
          const pinHash: string | undefined = data.settingsLock?.pinHash;
          const pinLength: number | undefined =
            data.settingsLock?.pinLength ?? undefined;
          if (
            pinHash !== license.settingsLock?.pinHash ||
            pinLength !== license.settingsLock?.pinLength
          ) {
            setLicense({
              ...license,
              settingsLock: pinHash ? { pinHash, pinLength } : undefined,
            });
          }
        }
      }
    } catch {
      // Offline or unreachable — not a session revocation, ignore.
    }
  }, [
    useNewApi,
    baseUrl,
    portalKey,
    effectiveType,
    signOut,
    license,
    setLicense,
  ]);

  useInterval(checkSession, sessionHeartbeatInterval);

  return null;
}
